/**
 * /me — the bootstrap call. After login or refresh, the frontend fires this
 * once to learn: who am I, which workspaces do I belong to, what apps do I
 * have in each.
 */

import { useQuery } from '@tanstack/react-query';
import { getApiClient } from '@clarix/api-client';
import { useAuthStore } from '@clarix/auth-store';
import type { MeResponse } from '@clarix/shared-types';
import { unwrap, type ApiResponse } from './envelope';
import { queryKeys } from './keys';

export interface UseMeOptions {
  activeSlug?: string;
  enabled?: boolean;
}

export function useMe(opts: UseMeOptions = {}) {
  const hasToken = useAuthStore((s) => !!s.token);
  return useQuery({
    queryKey: opts.activeSlug ? [...queryKeys.me(), opts.activeSlug] : queryKeys.me(),
    queryFn: async (): Promise<MeResponse> => {
      const res = await getApiClient().get<ApiResponse<MeResponse>>('/me', {
        params: opts.activeSlug ? { active: opts.activeSlug } : undefined,
      });
      return unwrap(res.data);
    },
    enabled: (opts.enabled ?? true) && hasToken,
    // A key change to the slug /me already answered for (single-host: the session slug landing
    // mid-boot) keeps that answer instead of blanking the shell. Another workspace's never carries.
    placeholderData: (prev) =>
      prev && opts.activeSlug && prev.active_workspace_slug === opts.activeSlug ? prev : undefined,
    staleTime: 60_000,
    // Session heartbeat: the gateway 401s a revoked token, so a deactivated user is
    // logged out within the interval in every app. Paused while the tab is hidden —
    // the focus refetch on return (data is stale after 60 s) catches a revoke then.
    refetchInterval: 20_000,
    refetchIntervalInBackground: false,
  });
}
